import React, { useState } from "react"
import { X } from "lucide-react"
import { sendEmail } from "../services/emailService"

const FreeTrialModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    workEmail: "",
    companyName: "",
    companySize: "",
    phoneNumber: "",
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success' | 'error' | null

  const companySizeOptions = [
    { value: "", label: "Select company size" },
    { value: "1-10", label: "1-10 employees" },
    { value: "11-50", label: "11-50 employees" },
    { value: "51-200", label: "51-200 employees" },
    { value: "201-500", label: "201-500 employees" },
    { value: "501-1000", label: "501-1000 employees" },
    { value: "1000+", label: "1000+ employees" },
  ]

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validation
    if (
      !formData.fullName ||
      !formData.workEmail ||
      !formData.companyName ||
      !formData.companySize
    ) {
      alert("Please fill in all required fields")
      return
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.workEmail)) {
      alert("Please enter a valid email address")
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      // Prepare email content
      const emailSubject = "New Free Trial Request - Resolut"
      const emailText = `
New Free Trial Request:

Name: ${formData.fullName}
Email: ${formData.workEmail}
Company: ${formData.companyName}
Company Size: ${formData.companySize}
Phone: ${formData.phoneNumber || "Not provided"}
      `.trim()

      const emailHtml = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #2563eb;">New Free Trial Request</h2>
          <div style="background-color: #f3f4f6; padding: 20px; border-radius: 8px; margin-top: 20px;">
            <p style="margin: 10px 0;"><strong>Name:</strong> ${formData.fullName}</p>
            <p style="margin: 10px 0;"><strong>Email:</strong> ${formData.workEmail}</p>
            <p style="margin: 10px 0;"><strong>Company:</strong> ${formData.companyName}</p>
            <p style="margin: 10px 0;"><strong>Company Size:</strong> ${formData.companySize}</p>
            <p style="margin: 10px 0;"><strong>Phone:</strong> ${formData.phoneNumber || "Not provided"}</p>
          </div>
          <p style="margin-top: 20px; color: #6b7280; font-size: 14px;">
            This request was submitted from the Resolut website free trial form.
          </p>
        </div>
      `

      await sendEmail({
        email: "akshay.kumar3389@gmail.com", // Your email where you want to receive notifications
        subject: emailSubject,
        text: emailText,
        html: emailHtml,
        type: "free-trial-request",
        senderName: formData.fullName,
        recipientEmail: formData.workEmail,
      })

      setSubmitStatus("success")

      // Reset form after 2 seconds and close modal
      setTimeout(() => {
        setFormData({
          fullName: "",
          workEmail: "",
          companyName: "",
          companySize: "",
          phoneNumber: "",
        })
        setSubmitStatus(null)
        onClose()
      }, 4000)
    } catch (error) {
      console.error("Error submitting free trial request:", error)
      setSubmitStatus("error")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        
        .free-trial-modal {
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
        }
      `}</style>

      <div className="free-trial-modal bg-white w-full max-w-lg rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-xl font-bold text-slate-900 tracking-tight">
              Start Your Free Trial
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Get 6 months free access to our platform
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors p-1.5 hover:bg-slate-100 rounded-lg"
            disabled={isSubmitting}
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5">
          <div className="space-y-4">
            {/* Full Name */}
            <div>
              <label
                htmlFor="fullName"
                className="block text-sm font-semibold text-slate-900 mb-1.5"
              >
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-all text-slate-900 placeholder:text-slate-400"
                placeholder="John Doe"
                disabled={isSubmitting}
              />
            </div>

            {/* Work Email */}
            <div>
              <label
                htmlFor="workEmail"
                className="block text-sm font-semibold text-slate-900 mb-1.5"
              >
                Work Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="workEmail"
                name="workEmail"
                value={formData.workEmail}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-all text-slate-900 placeholder:text-slate-400"
                placeholder="john@company.com"
                disabled={isSubmitting}
              />
            </div>

            {/* Company Name */}
            <div>
              <label
                htmlFor="companyName"
                className="block text-sm font-semibold text-slate-900 mb-1.5"
              >
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-all text-slate-900 placeholder:text-slate-400"
                placeholder="Acme Corporation"
                disabled={isSubmitting}
              />
            </div>

            {/* Company Size */}
            <div>
              <label
                htmlFor="companySize"
                className="block text-sm font-semibold text-slate-900 mb-1.5"
              >
                Company Size <span className="text-red-500">*</span>
              </label>
              <select
                id="companySize"
                name="companySize"
                value={formData.companySize}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-all text-slate-900 bg-white"
                disabled={isSubmitting}
              >
                {companySizeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Phone Number (Optional) */}
            <div>
              <label
                htmlFor="phoneNumber"
                className="block text-sm font-semibold text-slate-900 mb-1.5"
              >
                Phone Number{" "}
                <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-all text-slate-900 placeholder:text-slate-400"
                placeholder="+1 (555) 000-0000"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Success Message */}
          {submitStatus === "success" && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-xs font-medium">
                ✓ Thank you! Your free trial request has been submitted
                successfully. We'll contact you soon, or you can reach out to{" "}
                <a
                  href="mailto:akshay@resolut.co.in"
                  className="underline hover:text-green-900"
                >
                  akshay@resolut.co.in
                </a>{" "}
                for support.
              </p>
            </div>
          )}

          {/* Error Message */}
          {submitStatus === "error" && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-xs font-medium">
                ✗ Something went wrong. Please try again or contact us directly.
              </p>
            </div>
          )}

          {/* Submit Button */}
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 hover:border-slate-300 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 text-sm bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default FreeTrialModal
